import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { activitiesAPI, tasksAPI, usersAPI, workplanAPI } from '../utils/api';
import { isAdminRole } from '../utils/rolesConfig';
import { useAuth } from '../hooks/useAuth';

import { useToast, apiError, useConfirm } from '../components/Toast';
import { format, parseISO, startOfWeek, addWeeks, subWeeks, addDays, subDays, isSameDay, subMonths, addMonths, getDaysInMonth, getDay } from 'date-fns';
import { CheckSquare, ChevronLeft, ChevronRight, Clock, Layers, LayoutGrid, Repeat, SquarePen, Trash2, Users } from 'lucide-react';
import { STATUS_OPTIONS, STATUS_STYLE, WORK_STATUSES } from '../utils/workStatus';

// ── Icons ──────────────────────────────────────────────────────────────────
const PenIc = () => <SquarePen size={11} />;
const TrashIc = () => <Trash2 size={11} />;
const UsersIc = () => <Users size={14} />;
const LayersIc = () => <Layers size={11} />;
const CheckIc = () => <CheckSquare size={11} />;

// ── Constants ──────────────────────────────────────────────────────────────
const HOUR_PX  = 64;
const TIME_COL = 56;
const HOURS    = Array.from({ length: 24 }, (_, i) => i);

const SC = {
  Planned:       { bg:'#eef2fa', border:'#2E539F', text:'#1f3d76' },
  'In Progress': { bg:'#fffbeb', border:'#f59e0b', text:'#b45309' },
  Done:          { bg:'#ecfdf5', border:'#10b981', text:'#065f46' },
  Hold:          { bg:'rgba(255,200,73,.16)', border:'#FFC849', text:'#92700f' },
};

// phase41: workplan_entries.status is a PLANNING state only. Completion lives
// on the activity or task, returned by the API as activity_status/task_status.
// A plan entry records what was PLANNED for a day, not whether it finished
// (phase41) — completion is read from the activity or task. It uses the same
// word as they do for the paused state rather than Blocked beside Hold.
const PLAN_STATUSES = ['Planned', 'In Progress', 'Hold'];
// Matches workplan_entries_recurrence_check (phase46).
// Working-day length comes from Admin > System Settings (working_day_hours),
// returned with the week as day_capacity. 8 is only the fallback.
const DEFAULT_DAY_CAPACITY = 8;
// day_of_week is 0-based from Monday, so 0-4 is Mon-Fri.
const DAY_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_LONG  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const WORKING_DAYS = new Set([0, 1, 2, 3, 4]);
const isDone = (e) => e?.activity_status === 'Completed' || e?.task_status === 'Completed';
const scOf   = (e) => (isDone(e) ? SC.Done : (SC[e?.status] || SC.Planned));

const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const timeToMins  = (t) => { if (!t) return null; const [h,m] = t.split(':').map(Number); return h*60+m; };
const minsToTime  = (n) => `${String(Math.floor(n/60)%24).padStart(2,'0')}:${String(n%60).padStart(2,'0')}`;

// ── Event card ─────────────────────────────────────────────────────────────
function EventCard({ entry, onEdit, onDelete, onStatusChange, onToggleDone, onConfirm, onCardClick, onDragStart }) {
  const sc      = scOf(entry);
  const label   = entry.activity_title || entry.task_title || entry.custom_title || '—';
  const sub     = entry.activity_title ? (entry.task_title || entry.project_name) : entry.project_name;
  const isAct   = !!entry.activity_id;
  const planned = parseFloat(entry.planned_hours) || 1;

  const sm = timeToMins(entry.start_time);
  const em = timeToMins(entry.end_time);
  const hasTime  = sm !== null && em !== null;
  const durMin   = hasTime ? Math.max(em - sm, 15) : null;
  const topPx    = hasTime ? (sm / 60) * HOUR_PX : 0;
  const heightPx = hasTime ? Math.max((durMin / 60) * HOUR_PX, 22) : null;
  const compact  = durMin !== null && durMin < 35;

  const pos = {
    position: 'absolute',
    top:      topPx,
    left:     3,
    right:    3,
    height:   heightPx,
    zIndex:   entry._displayOnly ? 5 : 10,
    overflow: 'hidden',
  };

  if (entry.is_virtual) {
    return (
      <div className="group cursor-pointer rounded-md px-2 py-1 select-none"
        style={{ ...pos, background:'rgba(46,83,159,.07)', border:'1.5px dashed rgba(46,83,159,.35)' }}
        onClick={() => onCardClick && onCardClick(entry)}>
        <div className="flex items-center justify-between gap-1 h-full overflow-hidden">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <span style={{ color:'#2E539F', flexShrink:0, opacity:.7 }}>{isAct ? <LayersIc/> : <CheckIc/>}</span>
            <span className="text-xs font-medium truncate" style={{ color:'#2E539F' }}>{label}</span>
          </div>
          {!compact && (
            <button onClick={e => { e.stopPropagation(); onConfirm(entry); }}
              className="text-xs rounded px-1.5 py-0.5 font-semibold flex-shrink-0 hover:opacity-90"
              style={{ background:'#2E539F', color:'#fff', fontSize:10 }}>
              Plan it
            </button>
          )}
        </div>
        {!compact && sub && (
          <p className="text-xs truncate" style={{ color:'rgba(46,83,159,.6)', fontSize:10 }}>{sub}</p>
        )}
      </div>
    );
  }

  return (
    <div className="group cursor-grab active:cursor-grabbing rounded-md overflow-hidden select-none"
      draggable
      onDragStart={onDragStart ? onDragStart(entry) : undefined}
      title="Drag to another day to move it"
      style={{ ...pos, background:sc.bg, borderLeft:`3px solid ${sc.border}`, boxShadow:'0 1px 3px rgba(0,0,0,.08)' }}
      onClick={() => onCardClick && onCardClick(entry)}>
      <div className="px-2 py-1 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1">
            <span style={{ color:sc.border, flexShrink:0 }}>{isAct ? <LayersIc/> : <CheckIc/>}</span>
            <span className="text-xs font-semibold truncate" style={{ color:sc.text }}>{label}</span>
          </div>
          {!compact && sub && (
            <p className="text-xs truncate mt-0.5" style={{ color:sc.text, opacity:.65, fontSize:10 }}>{sub}</p>
          )}
        </div>
        {!compact && hasTime && (
          <p className="text-xs" style={{ color:sc.text, opacity:.6, fontSize:10 }}>
            {fmt12(entry.start_time)}{entry.end_time ? ` – ${fmt12(entry.end_time)}` : ''} · {planned.toFixed(1)}h
          </p>
        )}
      </div>
      <div className="absolute inset-y-0 right-0 hidden group-hover:flex flex-col items-center justify-center gap-0.5 px-0.5 rounded-r"
        style={{ background:sc.border, width:20 }}
        onClick={e => e.stopPropagation()}>
        {/* The rule lives on the activity now, so the badge reads it from
            there rather than from the entry, which never carries one. */}
        {entry.is_recurring && (
          <span className="text-[9px] px-1 rounded bg-white/25 text-white flex-shrink-0"
            title="Repeats — set on the activity">↻</span>
        )}
        <button onClick={() => onToggleDone(entry)} title={isDone(entry) ? 'Reopen' : 'Mark done'}
          className="w-4 h-4 rounded flex items-center justify-center bg-white/20 hover:bg-white/40 text-white"
          style={{ fontSize:9 }}>{isDone(entry) ? '↺' : '✓'}</button>
        <button onClick={() => onEdit(entry)}
          className="w-4 h-4 rounded flex items-center justify-center bg-white/20 hover:bg-white/40 text-white"><PenIc/></button>
        <button onClick={() => onDelete(entry.id)}
          className="w-4 h-4 rounded flex items-center justify-center bg-white/20 hover:bg-white/40 text-white"><TrashIc/></button>
      </div>
    </div>
  );
}

// ── Detail popup ───────────────────────────────────────────────────────────
function DetailModal({ entry, data, loading, onClose, onStatusChange, onToggleDone }) {
  const ref = useRef();
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const sc    = scOf(entry);
  const label = data?.title || entry.activity_title || entry.task_title || entry.custom_title || '—';
  const isAct = !!entry.activity_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div ref={ref} className="relative rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden"
        style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
        <div className="px-5 py-4 border-b flex items-start justify-between gap-3"
          style={{ borderColor:'var(--border)', borderLeft:`4px solid ${sc.border}` }}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background:sc.bg, color:sc.text }}>{isAct ? 'Activity' : 'Task'}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background:sc.bg, color:sc.text }}>{entry.status}</span>
            </div>
            <h3 className="font-bold text-base" style={{ color:'var(--text)' }}>{loading ? 'Loading…' : label}</h3>
            {(data?.project_name || entry.project_name) && (
              <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{data?.project_name || entry.project_name}</p>
            )}
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-lg hover:bg-[var(--surface-alt)]"
            style={{ color:'var(--text-secondary)', flexShrink:0 }}>×</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
          ) : data ? (
            <>
              {data.description && <p className="text-sm" style={{ color:'var(--text)' }}>{data.description}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {data.priority    && <div><p className="text-xs font-semibold uppercase" style={{ color:'var(--text-secondary)' }}>Priority</p><p style={{ color:'var(--text)' }}>{data.priority}</p></div>}
                {data.due_date    && <div><p className="text-xs font-semibold uppercase" style={{ color:'var(--text-secondary)' }}>Due</p><p style={{ color:'var(--text)' }}>{format(new Date(data.due_date),'MMM d, yyyy')}</p></div>}
                {data.estimated_hours && <div><p className="text-xs font-semibold uppercase" style={{ color:'var(--text-secondary)' }}>Est. Hours</p><p style={{ color:'var(--text)' }}>{data.estimated_hours}h</p></div>}
                {data.assigned_to_name && <div><p className="text-xs font-semibold uppercase" style={{ color:'var(--text-secondary)' }}>Assigned To</p><p style={{ color:'var(--text)' }}>{data.assigned_to_name}</p></div>}
              </div>
              {entry.start_time && (
                <div className="p-2 rounded-lg" style={{ background:'var(--surface-alt)' }}>
                  <p className="text-sm font-medium" style={{ color:'var(--text)' }}>
                    🕐 {fmt12(entry.start_time)}{entry.end_time ? ` – ${fmt12(entry.end_time)}` : ''}
                  </p>
                </div>
              )}
              {!entry.is_virtual && (
                <div className="pt-2 border-t" style={{ borderColor:'var(--border)' }}>
                  <p className="text-xs font-semibold uppercase mb-2" style={{ color:'var(--text-secondary)' }}>Update Status</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {PLAN_STATUSES.map(s => (
                      <button key={s} onClick={() => { onStatusChange(entry, s); onClose(); }}
                        className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors"
                        style={{ background:entry.status===s?SC[s]?.bg:'transparent', color:entry.status===s?SC[s]?.text:'var(--text-secondary)', borderColor:entry.status===s?SC[s]?.border:'var(--border)' }}>
                        {s}
                      </button>
                    ))}
                    <button onClick={() => { onToggleDone(entry); onClose(); }}
                      className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors"
                      style={{ background:isDone(entry)?SC.Done.bg:'transparent',
                               color:isDone(entry)?SC.Done.text:'var(--text-secondary)',
                               borderColor:isDone(entry)?SC.Done.border:'var(--border)' }}>
                      {isDone(entry) ? 'Reopen' : 'Mark done'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : <p className="text-sm text-center py-4" style={{ color:'var(--text-secondary)' }}>Could not load details</p>}
        </div>
        <div className="px-5 py-3 border-t flex justify-between items-center" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
          <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Click outside to close</p>
          {(entry.task_id || entry.activity_id) && (
            <Link to={entry.activity_id ? `/activities/${entry.activity_id}` : `/tasks/${entry.task_id}`}
              className="text-xs font-medium text-brand-500 hover:underline">Open full details →</Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function WorkplanPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const { user } = useAuth();
  // Admin level only (Administrator + Admin) — never compare role to a literal.
  const isMgr = isAdminRole(user?.role);

  // Pipeline is the default: 'what am I doing this week' is the question
  // this page answers most often. The time grid is one click away.
  const [viewMode,    setViewMode]    = useState('pipeline');
  const [error, setError] = useState('');
  const [anchor,      setAnchor]      = useState(() => startOfWeek(new Date(), { weekStartsOn:1 }));
  const [data,        setData]        = useState(null);
  const [monthData,   setMonthData]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [teamData,    setTeamData]    = useState([]);
  const [selUserId,   setSelUserId]   = useState(null);
  const [allUsers,    setAllUsers]    = useState([]);
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailData,  setDetailData]  = useState(null);
  const [detailLoad,  setDetailLoad]  = useState(false);
  const [addingDay,   setAddingDay]   = useState(null);
  const [editEntry,   setEditEntry]   = useState(null);
  const [form,        setForm]        = useState({
    task_id:'', activity_id:'', custom_title:'',
    planned_hours:'1', notes:'', status:'Planned',
    start_time:'', end_time:'', start_date:'', due_date:'',
    recurrence:'none', recurrence_until:'',
    useSpan:false, spanDays:[], hoursPerDay:'2',
  });
  const [saving,     setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // entry to delete
  const scrollRef = useRef(null);

  // Compute visible days — use actual date's day-of-week, not loop index
  const visibleDays = (() => {
    if (viewMode === 'day')   return [anchor];
    if (viewMode === '3day')  return [anchor, addDays(anchor,1), addDays(anchor,2)];
    if (viewMode === 'week' || viewMode === 'pipeline') {
      const mon = startOfWeek(anchor, { weekStartsOn:1 });
      return Array.from({ length:7 }, (_,i) => addDays(mon, i));
    }
    return [];
  })();

  const weekStart   = format(startOfWeek(anchor, { weekStartsOn:1 }), 'yyyy-MM-dd');
  const thisMonStart = format(startOfWeek(new Date(), { weekStartsOn:1 }), 'yyyy-MM-dd');
  const isCurrent   = weekStart === thisMonStart;

  const isViewingToday = (() => {
    const t = new Date();
    if (viewMode==='day')   return isSameDay(anchor, t);
    if (viewMode==='3day')  return visibleDays.some(d => isSameDay(d, t));
    if (viewMode==='week'||viewMode==='pipeline')  return isCurrent;
    if (viewMode==='month') return anchor.getFullYear()===t.getFullYear() && anchor.getMonth()===t.getMonth();
    return false;
  })();

  // Scroll to current time on mount + view switch (all time views)
  useEffect(() => {
    if (viewMode==='month' || viewMode==='team') return;
    const now = new Date();
    const scrollTop = Math.max(0, (now.getHours() + now.getMinutes()/60 - 0.5) * HOUR_PX);
    requestAnimationFrame(() => {
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollTop; }, 60);
    });
  }, [viewMode, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const uid = selUserId || user?.id;
      if (viewMode==='month') {
        const r = await workplanAPI.getMonth({ year:anchor.getFullYear(), month:anchor.getMonth()+1, user_id:uid });
        setMonthData(r.data); setData(null);
      } else if (viewMode==='team') {
        const r = await workplanAPI.getTeam({ week_start:weekStart });
        setTeamData(r.data||[]); setData(null); setMonthData(null);
      } else {
        const r = await workplanAPI.get({ week_start:weekStart, user_id:uid });
        setData(r.data); setMonthData(null);
      }
    } catch(e) { setError(e.response?.data?.message || 'Failed to load data'); }
    finally { setLoading(false); }
  }, [viewMode, anchor, weekStart, selUserId, user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isMgr) usersAPI.getAll().then(r=>setAllUsers(r.data||[])).catch(e => toast.error(apiError(e, 'Could not load this page.'))); }, [isMgr]);

  // Navigation
  const goBack = () => {
    if (viewMode==='day')   setAnchor(a => subDays(a,1));
    if (viewMode==='3day')  setAnchor(a => subDays(a,3));
    if (viewMode==='week'||viewMode==='pipeline')  setAnchor(a => subWeeks(a,1));
    if (viewMode==='month') setAnchor(a => subMonths(a,1));
  };
  const goNext = () => {
    if (viewMode==='day')   setAnchor(a => addDays(a,1));
    if (viewMode==='3day')  setAnchor(a => addDays(a,3));
    if (viewMode==='week'||viewMode==='pipeline')  setAnchor(a => addWeeks(a,1));
    if (viewMode==='month') setAnchor(a => addMonths(a,1));
  };
  const goToday = () => {
    const t = new Date();
    if (viewMode==='day'||viewMode==='3day') setAnchor(t);
    if (viewMode==='week'||viewMode==='pipeline')  setAnchor(startOfWeek(t, { weekStartsOn:1 }));
    if (viewMode==='month') setAnchor(new Date(t.getFullYear(), t.getMonth(), 1));
  };
  // Switching view does NOT reset anchor — only Today button does
  const switchView = (m) => {
    setViewMode(m);
    if (m==='week')  setAnchor(a => startOfWeek(a, { weekStartsOn:1 }));
    if (m==='month') setAnchor(a => new Date(a.getFullYear(), a.getMonth(), 1));
  };

  const rangeLabel = (() => {
    const t = new Date();
    if (viewMode==='day') {
      const prefix = isSameDay(anchor,t) ? 'Today · '
        : isSameDay(anchor,addDays(t,1)) ? 'Tomorrow · '
        : isSameDay(anchor,subDays(t,1)) ? 'Yesterday · ' : '';
      return prefix + format(anchor,'EEEE, MMMM d yyyy');
    }
    if (viewMode==='3day') {
      const p = visibleDays.some(d=>isSameDay(d,t)) ? 'This 3 days · ' : '';
      return p + `${format(anchor,'EEE MMM d')} – ${format(addDays(anchor,2),'EEE MMM d, yyyy')}`;
    }
    if (viewMode==='week'||viewMode==='pipeline') {
      const mon = startOfWeek(anchor,{weekStartsOn:1});
      const p = isCurrent ? 'This week · ' : '';
      return p + `${format(mon,'MMM d')} – ${format(addDays(mon,6),'MMM d, yyyy')}`;
    }
    if (viewMode==='month') return format(anchor,'MMMM yyyy');
    return 'Team Workplan';
  })();

  // CRUD handlers
  const openAdd = (dayIdx, timeStr='') => {
    setAddingDay(dayIdx); setEditEntry(null);
    const endTime = timeStr ? minsToTime(timeToMins(timeStr)+60) : '';
    setForm({ task_id:'', activity_id:'', custom_title:'', planned_hours:'1', start_date:'', due_date:'',
      notes:'', status:'Planned',
      start_time:timeStr, end_time:endTime, useSpan:false, spanDays:[dayIdx], hoursPerDay:'2' });
  };
  const openEdit = (entry) => {
    setEditEntry(entry); setAddingDay(entry.day_of_week);
    setForm({ task_id:entry.task_id||'', activity_id:entry.activity_id||'',
      custom_title:entry.custom_title||'', planned_hours:entry.planned_hours||'1',
      notes:entry.notes||'', status:entry.status||'Planned',
      start_time:(entry.start_time||'').slice(0,5), end_time:(entry.end_time||'').slice(0,5),
      start_date:(entry.start_date||'').slice(0,10), due_date:(entry.due_date||'').slice(0,10),
      useSpan:false, spanDays:[], hoursPerDay:'2' });
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const uid = selUserId||user?.id;
      const base = {
        week_start:weekStart, user_id:uid,
        // One kind of entry: an activity. The task comes from the activity,
        // so it is never sent and never set on the row.
        task_id:      null,
        activity_id:  form.activity_id,
        custom_title: null,
        notes:form.notes, status:form.status,
        start_time:form.start_time||null, end_time:form.end_time||null,
      };
      let hours = parseFloat(form.planned_hours)||1;
      if (form.start_time && form.end_time) {
        const diff = (timeToMins(form.end_time)-timeToMins(form.start_time))/60;
        if (diff>0) hours = Math.round(diff*10)/10;
      }
      if (editEntry) {
        await workplanAPI.update(editEntry.id, { ...base, day_of_week:addingDay, planned_hours:hours });
        toast.success('Plan entry saved');
        // Dates live on the task/activity itself, not on the workplan entry.
        const dates = { start_date:form.start_date||null, due_date:form.due_date||null };
        const changed = (form.start_date||'') !== (editEntry.start_date||'').slice(0,10)
                     || (form.due_date||'')   !== (editEntry.due_date||'').slice(0,10);
        if (changed) {
          if (editEntry.activity_id)  await workplanAPI.updateActivityDate(editEntry.activity_id, dates);
          else if (editEntry.task_id) await workplanAPI.updateTaskDate(editEntry.task_id, dates);
        }
      } else if (form.useSpan && form.spanDays.length>0) {
        await Promise.all(form.spanDays.map(d => workplanAPI.create({ ...base, day_of_week:d, planned_hours:parseFloat(form.hoursPerDay)||1 })));
      } else {
        await workplanAPI.create({ ...base, day_of_week:addingDay, planned_hours:hours });
        toast.success('Plan entry saved');
      }
      setAddingDay(null); setEditEntry(null); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save workplan entry.')); }
    finally { setSaving(false); }
  };
  const handleDelete  = (id)    => { setConfirmDel(id); };
  const confirmDelete = async () => {
    if (!confirmDel) return;
    try { await workplanAPI.delete(confirmDel);
    toast.success('Plan entry deleted'); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to delete workplan entry.')); }
    finally { setConfirmDel(null); }
  };
  const handleToggleDone = async (entry) => {
    if (entry.is_virtual) return; // nothing planned yet
    const next = isDone(entry) ? 'In Progress' : 'Completed';
    try {
      if (entry.activity_id)  await activitiesAPI.update(entry.activity_id, { status: next });
      else if (entry.task_id) await tasksAPI.update(entry.task_id, { status: next });
      else return; // a custom entry has no record to complete
      toast.success(next === 'Completed' ? 'Marked done' : 'Reopened');
    } catch(e) { toast.error(apiError(e, 'Failed to update.')); }
    finally { load(); }
  };

  const handleStatus  = async (entry, status) => {
    // MUST include task_id and activity_id — the PUT SQL sets them directly,
    // so omitting them clears the association and the entry loses its title.
    if (entry.is_virtual) return; // virtual items have no real DB row to update
    try {
      await workplanAPI.update(entry.id, {
        status,
        planned_hours: entry.planned_hours,
        day_of_week:   entry.day_of_week,
        task_id:       entry.task_id       || null,
        activity_id:   entry.activity_id   || null,
      });
      toast.success('Plan entry updated');
    } catch(e) { toast.error(apiError(e, 'Failed to update status.')); }
    finally { load(); }
  };
  const [copying, setCopying] = useState(false);
  const [dragOverDay, setDragOverDay] = useState(null);
  // Set on dragstart, read on drop — a ref, because changing it must not
  // re-render mid-drag.
  const dragGrabOffset = useRef(0);

  /** Deadlines falling on each visible day, whatever day the work is planned
   *  for. Keyed by YYYY-MM-DD. */
  const dueByDate = useMemo(() => {
    const map = {};
    for (const d of (data?.days || [])) {
      for (const e of (d.entries || [])) {
        const due = (e.due_date || '').slice(0, 10);
        if (!due) continue;
        const title = e.activity_title || e.task_title || e.custom_title || 'Untitled';
        (map[due] = map[due] || []).push({ id: e.id, title });
      }
    }
    // The same activity can be planned on several days; show it once per date.
    for (const k of Object.keys(map)) {
      const seen = new Set();
      map[k] = map[k].filter(x => !seen.has(x.title) && seen.add(x.title));
    }
    return map;
  }, [data]);

  /** Moving a card between days changes the plan entry's day_of_week — and
   *  with it plan_date, which the To-Do list reads. Virtual cards have no row
   *  to move, so they are not draggable. */
  const handleDragStart = (entry) => (e) => {
    if (entry.is_virtual) { e.preventDefault(); return; }
    e.dataTransfer.setData('text/plain', entry.id);
    e.dataTransfer.effectAllowed = 'move';
    // Where inside the card the pointer grabbed it. Without this the card
    // jumps so its TOP lands under the cursor, which is not where you aimed.
    const rect = e.currentTarget.getBoundingClientRect();
    dragGrabOffset.current = e.clientY - rect.top;
  };

  /** Where the card lands, in minutes from midnight, snapped to 30. */
  const dropTime = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top - (dragGrabOffset.current || 0);
    const snapped = Math.round((y / HOUR_PX * 60) / 30) * 30;
    return Math.min(Math.max(snapped, 0), 23 * 60 + 30);
  };

  const handleDrop = async (e, dow) => {
    e.preventDefault();
    setDragOverDay(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const entry = (data?.days || []).flatMap(d => d.entries).find(x => x.id === id);
    if (!entry) return;

    // Dropping sets the DAY and the TIME. Moving a card to Friday 08:00 and
    // leaving the record saying Monday 11:00 made the board lie about what
    // you had just done.
    const startMins = dropTime(e);
    const oldStart  = timeToMins(entry.start_time);
    const oldEnd    = timeToMins(entry.end_time);
    // Keep however long the work was; default to an hour if it had no span.
    const duration  = (oldStart != null && oldEnd != null && oldEnd > oldStart)
      ? oldEnd - oldStart : 60;
    // 24*60 would format as 00:00 and read as the start of the day.
    const endMins   = Math.min(startMins + duration, 23 * 60 + 59);

    const sameDay  = entry.day_of_week === dow;
    const sameTime = oldStart === startMins;
    if (sameDay && sameTime) return;          // nothing actually moved

    const payload = {
      day_of_week: dow,
      week_start:  weekStart,
      start_time:  minsToTime(startMins),
      end_time:    minsToTime(endMins),
    };

    // Move it on screen straight away; the reload reconciles.
    setDragOverDay(null);
    try {
      const r = await workplanAPI.update(id, payload);
      const warn = r.data?.plan_warnings?.[0];
      if (warn) {
        // It saved — planning outside the window is allowed — but say so, or
        // you would only find out when the deadline passed.
        toast.warning(warn, { title:'Scheduled outside the window' });
      } else {
        toast.success(
          `Moved to ${DAY_LONG[dow] || 'that day'} at ${fmt12(payload.start_time)}`);
      }
      load();
    } catch(err) {
      toast.error(apiError(err, 'Could not move that entry.'));
      load();                                  // put it back where it was
    }
  };

  const handleCopyLastWeek = async () => {
    const prev = format(subWeeks(new Date(weekStart), 1), 'yyyy-MM-dd');
    if (!await confirm(`Copy the plan from week of ${prev} into this week?\n\nFinished work is skipped, and anything already planned is left alone.`,
      { danger:false, title:'Copy last week', confirmLabel:'Copy' })) return;
    setCopying(true);
    try {
      const r = await workplanAPI.copyWeek({ from_week: prev, to_week: weekStart,
                                             user_id: selUserId || undefined });
      if (r.data.copied) toast.success(`Copied ${r.data.copied} entr${r.data.copied===1?'y':'ies'} from last week`);
      else               toast.info('Nothing to copy — last week was empty or already finished');
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to copy last week.')); }
    finally { setCopying(false); }
  };

  const handleConfirm = async (entry) => {
    try {
      await workplanAPI.confirm({ week_start:weekStart,
        day_of_week:entry.day_of_week, task_id:entry.task_id||null, activity_id:entry.activity_id||null,
        planned_hours:parseFloat(entry.planned_hours)||1 });
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to confirm workplan entry.')); }
  };
  const handleCardClick = async (entry) => {
    setDetailEntry(entry); setDetailData(null); setDetailLoad(true);
    try {
      if (entry.activity_id) { const r = await activitiesAPI.getById(entry.activity_id); setDetailData({type:'activity',...r.data}); }
      else if (entry.task_id) { const r = await tasksAPI.getById(entry.task_id); setDetailData({type:'task',...r.data}); }
    } catch(e) { setError(e.response?.data?.message || 'Failed to load data'); }
    finally { setDetailLoad(false); }
  };
  const handleGridClick = (e, dayIdx) => {
    // Prevent opening modal when clicking on an event card
    if (e.target.closest('.group')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const snapMins = Math.round((y / HOUR_PX * 60) / 30) * 30;
    openAdd(dayIdx, minsToTime(Math.min(Math.max(snapMins,0), 23*60)));
  };

  // Month view
  const MonthView = () => {
    if (!monthData) return null;
    const yr=anchor.getFullYear(), mo=anchor.getMonth();
    const first=new Date(yr,mo,1);
    const days=getDaysInMonth(first);
    let startDow=getDay(first); startDow=startDow===0?6:startDow-1;
    const cells=[]; for(let i=0;i<startDow;i++) cells.push(null);
    for(let d=1;d<=days;d++) cells.push(new Date(yr,mo,d));
    const weeks=[]; for(let i=0;i<cells.length;i+=7) weeks.push(cells.slice(i,i+7));
    return (
      <div className="rounded-xl overflow-hidden flex-1" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
        <div className="grid grid-cols-7 border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
          {DAY_SHORT.map(d=>(
            <div key={d} className="py-2 text-center text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{d}</div>
          ))}
        </div>
        {weeks.map((wk,wi)=>(
          <div key={wi} className="grid grid-cols-7 border-b" style={{ borderColor:'var(--border)', minHeight:96 }}>
            {Array.from({length:7},(_,di)=>{
              const date=wk[di];
              if (!date) return <div key={di} className="border-r p-1" style={{ borderColor:'var(--border)', opacity:.4, background:'var(--surface-alt)' }}/>;
              const isToday=isSameDay(date,new Date());
              const ds=format(date,'yyyy-MM-dd');
              const items=[
                ...(monthData.tasks||[]).filter(t=>t.due_date?.slice(0,10)===ds).map(t=>({label:t.title,color:'#2E539F'})),
                ...(monthData.activities||[]).filter(a=>a.due_date?.slice(0,10)===ds).map(a=>({label:a.title,color:'#5CC3BC'})),
                ...(monthData.entries||[]).filter(e=>{ try{return isSameDay(addDays(new Date(e.week_start),e.day_of_week),date);}catch{return false;} }).map(e=>({label:e.activity_title||e.task_title||e.custom_title||'Entry',color:'#2E539F'})),
              ];
              return (
                <div key={di} className="border-r p-1.5 cursor-pointer hover:bg-[var(--surface-alt)] transition-colors"
                  style={{ borderColor:'var(--border)', background:isToday?'rgba(46,83,159,.05)':undefined }}
                  onClick={()=>{setAnchor(date);switchView('day');}}>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold mb-1 ${isToday?'bg-brand-500 text-white':''}`}
                    style={!isToday?{color:'var(--text)'}:{}}>{date.getDate()}</div>
                  {items.slice(0,3).map((item,ii)=>(
                    <div key={ii} className="text-xs truncate rounded px-1 py-0.5 mb-0.5"
                      style={{ background:item.color+'22', color:item.color, fontSize:10 }}>{item.label}</div>
                  ))}
                  {items.length>3 && <div className="text-xs" style={{ color:'var(--text-secondary)', fontSize:10 }}>+{items.length-3} more</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // ── Pipeline view ────────────────────────────────────────────────────────
  // Seven columns, one per day, each a stack of cards. No time axis: this is
  // the view for "what am I doing this week", where the time grid answers
  // "when exactly". Both read the same entries, so nothing is duplicated and
  // switching between them never shows two different weeks.
  //
  // The status control on a card drives the ACTIVITY or TASK, not the plan
  // entry — the five statuses are what a person means by "where is this", and
  // the entry's own Planned/In Progress/Hold only describes the plan. A
  // free-text entry has no record behind it, so it falls back to the entry.

  /** What the card's dropdown should show, and which vocabulary it uses. */
  const recordStatusOf = (entry) =>
    entry.activity_id ? (entry.activity_status || 'Pending')
    : entry.task_id   ? (entry.task_status     || 'Pending')
    : (entry.status || 'Planned');

  /** The three answers a repeating day can have. Null means "not yet". */
  const OCCURRENCE_OPTS = [
    { v:'',        l:'Not yet'  },
    { v:'done',    l:'Done'     },
    { v:'skipped', l:'Skipped'  },
  ];

  const handleRecordStatus = async (entry, status) => {
    if (entry.is_virtual) return;            // nothing planned yet

    // A repeating card is ONE DAY of a standing duty. Its dropdown answers
    // for that day and must never touch the activity's own status — the duty
    // continues tomorrow whatever happened today.
    if (entry.is_recurring) {
      try {
        await workplanAPI.setOccurrence({
          activity_id:     entry.activity_id || undefined,
          entry_id:        entry.activity_id ? undefined : entry.entry_id,
          occurrence_date: entry.plan_date,
          status:          status || null,
        });
        toast.success(status === 'skipped' ? 'Marked skipped for that day'
                    : status === 'done'    ? 'Marked done for that day'
                    : 'Cleared');
      } catch(e) { toast.error(apiError(e, 'Could not update that day.')); }
      finally { load(); }
      return;
    }

    try {
      if (entry.activity_id)  await activitiesAPI.update(entry.activity_id, { status });
      else if (entry.task_id) await tasksAPI.update(entry.task_id, { status });
      else await workplanAPI.update(entry.id, {   // custom entry: the plan IS the record
        status,
        planned_hours: entry.planned_hours,
        day_of_week:   entry.day_of_week,
        task_id:       null,
        activity_id:   null,
      });
      toast.success('Status updated');
    } catch(e) { toast.error(apiError(e, 'Failed to update status.')); }
    finally { load(); }
  };

  /** A pipeline drop changes the DAY only — there is no time axis to read. */
  const handlePipelineDrop = async (e, dow) => {
    e.preventDefault();
    setDragOverDay(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const entry = (data?.days || []).flatMap(d => d.entries).find(x => x.id === id);
    if (!entry || entry.day_of_week === dow) return;
    try {
      await workplanAPI.update(entry.id, {
        day_of_week: dow,
        week_start:  weekStart,
        planned_hours: entry.planned_hours,
        task_id:     entry.task_id     || null,
        activity_id: entry.activity_id || null,
      });
      toast.success(`Moved to ${DAY_SHORT[dow]}`);
    } catch(err) { toast.error(apiError(err, 'Could not move that entry.')); }
    finally { load(); }
  };

  const PipelineView = () => {
    const days = Array.from({ length: 7 }, (_, i) => i);
    const entriesFor = (dow) => (data?.days?.[dow]?.entries || []).filter(e => !e.is_virtual);
    const all = days.flatMap(entriesFor);
    const total   = all.length;
    const done    = all.filter(isDone).length;
    const planned = all.reduce((s, e) => s + parseFloat(e.planned_hours || 0), 0);
    const rate    = total ? Math.round((done / total) * 100) : 0;

    const stats = [
      { v: `${done}/${total}`,            l: 'Activities Done',  c: '#2E539F' },
      { v: `${rate}%`,                    l: 'Completion Rate',  c: '#5CC3BC' },
      { v: `${planned.toFixed(1)}h`,      l: 'Hours Planned',    c: '#FFC849' },
    ];

    return (
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-shrink-0">
          {stats.map(s => (
            <div key={s.l} className="card text-center py-5">
              <p className="text-3xl font-bold" style={{ color: s.c }}>{s.v}</p>
              <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>{s.l}</p>
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="grid gap-3 h-full" style={{ gridTemplateColumns:'repeat(7, minmax(190px, 1fr))' }}>
            {days.map(dow => {
              const date    = addDays(parseISO(weekStart), dow);
              const today   = isSameDay(date, new Date());
              const items   = entriesFor(dow);
              const dayDone = items.filter(isDone).length;
              const over    = dragOverDay === dow;
              return (
                <div key={dow}
                  onDragOver={e => { e.preventDefault(); setDragOverDay(dow); }}
                  onDragLeave={() => setDragOverDay(d => d === dow ? null : d)}
                  onDrop={e => handlePipelineDrop(e, dow)}
                  onClick={e => { if (e.target === e.currentTarget) openAdd(dow); }}
                  className="rounded-xl flex flex-col min-h-0 cursor-pointer transition-colors"
                  style={{ background:'var(--surface)',
                           border:`1px solid ${over ? '#2E539F' : today ? 'rgba(46,83,159,.45)' : 'var(--border)'}`,
                           boxShadow: over ? '0 0 0 2px rgba(46,83,159,.18)' : '0 1px 3px rgba(0,0,0,.05)' }}>

                  <div className="px-3 py-2.5 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
                    <p className="text-[11px] font-semibold uppercase tracking-widest"
                      style={{ color: today ? '#2E539F' : 'var(--text-secondary)' }}>
                      {format(date,'EEE').toUpperCase()}
                    </p>
                    <p className="text-2xl font-bold leading-tight"
                      style={{ color: today ? '#2E539F' : 'var(--text)' }}>{format(date,'d')}</p>
                    <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                      {dayDone}/{items.length}
                    </p>
                  </div>

                  <div className="p-2 flex flex-col gap-2 overflow-y-auto flex-1 min-h-0">
                    {items.length === 0 ? (
                      <p className="text-sm text-center py-6 select-none"
                        style={{ color:'var(--text-secondary)', opacity:.6 }}>Free</p>
                    ) : items.map(entry => {
                      const rec  = entry.is_recurring;
                      const st   = rec ? (entry.occurrence_status || '') : recordStatusOf(entry);
                      const fin  = rec ? entry.occurrence_done : isDone(entry);
                      const sty  = rec ? null : STATUS_STYLE[st];
                      const opts = rec ? OCCURRENCE_OPTS
                        : WORK_STATUSES.map(v => ({ v, l: v }));
                      return (
                        <div key={entry.card_key || entry.id}
                          draggable={!entry.is_recurring}
                          onDragStart={handleDragStart(entry)}
                          onClick={() => handleCardClick(entry)}
                          className="rounded-lg p-2.5 cursor-pointer transition-shadow hover:shadow-md"
                          style={{ background: fin ? 'rgba(92,195,188,.10)' : 'var(--surface)',
                                   border:`1px solid ${fin ? '#5CC3BC' : 'var(--border)'}` }}>
                          <p className="text-sm font-medium leading-snug"
                            style={{ color:'var(--text)' }}>
                            {entry.activity_title || entry.task_title || entry.custom_title || 'Untitled'}
                          </p>
                          {entry.activity_title && entry.task_title && (
                            <p className="text-xs mt-0.5 truncate" style={{ color:'var(--text-secondary)' }}>
                              {entry.task_title}
                            </p>
                          )}
                          <p className="text-xs mt-1 flex items-center gap-1" style={{ color:'var(--text-secondary)' }}>
                            <Clock size={11}/>{parseFloat(entry.planned_hours || 0).toFixed(2)}h
                            {entry.is_recurring && <><Repeat size={11} className="ml-1"/>repeats</>}
                          </p>
                          {/* Stops at the card so choosing a status does not
                              also open the detail modal behind the dropdown. */}
                          <select
                            className="select mt-2 text-xs w-full"
                            value={opts.some(o => o.v === st) ? st : opts[0].v}
                            onClick={e => e.stopPropagation()}
                            onChange={e => { e.stopPropagation(); handleRecordStatus(entry, e.target.value); }}
                            style={{ borderColor: sty?.border || (rec && st==='skipped' ? '#d97706' : 'var(--border)'),
                                     color: sty?.text || (rec && st==='skipped' ? '#b45309' : 'var(--text)') }}>
                            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const now    = new Date();
  const nowTop = (now.getHours()+now.getMinutes()/60) * HOUR_PX;
  const s      = data?.summary;

  return (
    <div className="flex flex-col gap-3" style={{ height:'calc(100vh - 120px)', minHeight:600 }}>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2 flex-shrink-0">
        <div>
          <h1 className="page-title">{viewMode==='pipeline' ? 'Weekly Workplan' : 'Workplan'}</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            {viewMode==='pipeline' ? 'Interactive pipeline view for the week' : rangeLabel}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isMgr && viewMode!=='team' && (
            <select className="select text-sm w-40" value={selUserId||''} onChange={e=>setSelUserId(e.target.value||null)}>
              <option value="">My Plan</option>
              {allUsers.filter(u=>u.id!==user?.id&&u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {viewMode!=='team' && viewMode!=='month' && (
            <button onClick={handleCopyLastWeek} disabled={copying}
              title="Copy last week's plan into this week"
              className="btn-secondary text-sm whitespace-nowrap">
              {copying ? 'Copying…' : 'Copy last week'}
            </button>
          )}
          {/* Today — standalone */}
          {viewMode!=='team' && (
            <button onClick={goToday}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all"
              style={{ color:isViewingToday?'var(--text-secondary)':'#2E539F', background:isViewingToday?'var(--surface-alt)':'rgba(46,83,159,.07)', borderColor:isViewingToday?'var(--border)':'rgba(46,83,159,.3)', cursor:isViewingToday?'default':'pointer' }}>
              {viewMode==='pipeline' ? 'This Week' : 'Today'}
            </button>
          )}
          {viewMode==='pipeline' && (
            <span className="text-sm font-medium px-1" style={{ color:'var(--text)' }}>{rangeLabel}</span>
          )}
          {/* ← [ Pipeline | Day | 3 Days | Week | Month ] → */}
          <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor:'var(--border)' }}>
            {viewMode!=='team' && (
              <button onClick={goBack}
                className="p-2 hover:bg-[var(--surface-alt)] border-r transition-colors"
                style={{ borderColor:'var(--border)', color:'var(--text)' }}
                title={viewMode==='day'?'Previous day':viewMode==='3day'?'Previous 3 days':viewMode==='week'?'Previous week':'Previous month'}>
                <ChevronLeft size={16}/>
              </button>
            )}
            {[
              {key:'pipeline',label:'Pipeline'},
              {key:'day',label:'Day'}, {key:'3day',label:'3 Days'},
              {key:'week',label:'Week'}, {key:'month',label:'Month'},
              ...(isMgr ? [{key:'team',label:'Team'}] : []),
            ].map(vm => (
              <button key={vm.key} onClick={()=>switchView(vm.key)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors border-r last:border-r-0 ${viewMode===vm.key?'bg-brand-500 text-white':''}`}
                style={viewMode!==vm.key?{color:'var(--text-secondary)',background:'var(--surface)',borderColor:'var(--border)'}:{borderColor:'transparent'}}>
                {vm.key==='team'?<span className="flex items-center gap-1"><UsersIc/>Team</span>
                :vm.key==='pipeline'?<span className="flex items-center gap-1"><LayoutGrid size={13}/>Pipeline</span>
                :vm.label}
              </button>
            ))}
            {viewMode!=='team' && (
              <button onClick={goNext}
                className="p-2 hover:bg-[var(--surface-alt)] border-l transition-colors"
                style={{ borderColor:'var(--border)', color:'var(--text)' }}
                title={viewMode==='day'?'Next day':viewMode==='3day'?'Next 3 days':viewMode==='week'?'Next week':'Next month'}>
                <ChevronRight size={16}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      {data && viewMode!=='month' && viewMode!=='team' && viewMode!=='pipeline' && (() => {
        // Stats computed from ONLY the visible days in the current view
        const visibleDows = new Set(visibleDays.map(d => {
          const dow = d.getDay(); return dow === 0 ? 6 : dow - 1;
        }));
        const visible = (data.days || [])
          .filter(day => visibleDows.has(day.day))
          .flatMap(day => day.entries || []);
        const committed = visible.filter(e => !e.is_virtual);
        const suggested = visible.filter(e =>  e.is_virtual);
        const total   = committed.length;
        const done    = committed.filter(isDone).length;
        const planned = committed.reduce((s,e) => s + parseFloat(e.planned_hours||0), 0);
        const sugHrs  = suggested.reduce((s,e) => s + parseFloat(e.planned_hours||0), 0);
        const label   = viewMode==='day' ? 'Today' : viewMode==='3day' ? '3 Days' : 'Week';
        const workDays = [...visibleDows].filter(d => WORKING_DAYS.has(d)).length;
        const capacity = workDays * (parseFloat(data.day_capacity) || DEFAULT_DAY_CAPACITY);
        const loadPct  = capacity ? Math.round((planned / capacity) * 100) : null;
        const over     = capacity > 0 && planned > capacity;
        return (
          <div className="grid grid-cols-4 gap-3 flex-shrink-0">
            {[
              {l:`Planned (${label})`, v: total,
               sub: suggested.length ? `${suggested.length} more suggested` : null},
              {l:'Done',               v: done},
              {l:'Hours planned',      v:`${planned.toFixed(1)}h`,
               sub: sugHrs ? `${sugHrs.toFixed(1)}h unplanned` : null},
              {l:'Capacity',
               v: loadPct === null ? '—' : `${loadPct}%`,
               sub: capacity ? `${planned.toFixed(1)} of ${capacity}h` : 'no working days in view',
               warn: over},
            ].map(c=>(
              <div key={c.l} className="rounded-xl px-4 py-2.5"
                style={{ background:'var(--surface)',
                         border:`1px solid ${c.warn ? '#d97706' : 'var(--border)'}` }}>
                <p className="text-lg font-bold" style={{ color: c.warn ? '#d97706' : 'var(--text)' }}>{c.v}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{c.l}</p>
                {c.sub && <p className="text-xs" style={{ color: c.warn ? '#d97706' : 'var(--text-secondary)', opacity:.8 }}>{c.sub}</p>}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Legend */}
      {viewMode!=='team' && viewMode!=='month' && viewMode!=='pipeline' && (
        <div className="flex items-center gap-4 text-xs flex-shrink-0" style={{ color:'var(--text-secondary)' }}>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2" style={{ borderStyle:'dashed', borderColor:'var(--border)' }}/>Auto-detected — click "Plan it"</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-brand-500"/>Planned — click to see details</span>
          <span className="ml-auto">Click any time slot to add</span>
        </div>
      )}

      {loading && <div className="flex justify-center py-20 flex-1"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>}

      {/* Pipeline */}
      {!loading && viewMode==='pipeline' && <PipelineView/>}

      {/* Month */}
      {!loading && viewMode==='month' && <MonthView/>}

      {/* Team */}
      {!loading && viewMode==='team' && (
        <div className="card overflow-hidden p-0 flex-1 flex flex-col">
          <div className="px-5 py-4 border-b flex items-center gap-2 flex-shrink-0" style={{ borderColor:'var(--border)' }}>
            <UsersIc/><h3 className="font-semibold text-sm" style={{ color:'var(--text)' }}>Team Overview</h3>
          </div>
          {teamData.length===0 ? (
            <p className="text-center py-8 text-sm flex-1" style={{ color:'var(--text-secondary)' }}>No team data</p>
          ) : (
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead><tr className="border-b" style={{ borderColor:'var(--border)' }}>
                  {['Member','Role','Entries','Done','Planned h','Actual h','Progress'].map(h=>(
                    <th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{teamData.map(u=>{
                  const pct=parseInt(u.total_entries)>0?Math.round((parseInt(u.done_entries)/parseInt(u.total_entries))*100):0;
                  return (
                    <tr key={u.id} className="border-b hover:bg-[var(--surface-alt)] cursor-pointer" style={{ borderColor:'var(--border)' }} onClick={()=>{setSelUserId(u.id);switchView('week');}}>
                      <td className="py-3 px-3"><div className="flex items-center gap-2"><div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-white text-xs" style={{ background:u.avatar_color||'#2E539F' }}>{u.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}</div><span className="font-medium" style={{ color:'var(--text)' }}>{u.name}</span></div></td>
                      <td className="py-3 px-3 text-xs" style={{ color:'var(--text-secondary)' }}>{u.role}</td>
                      <td className="py-3 px-3 text-center" style={{ color:'var(--text)' }}>{u.total_entries}</td>
                      <td className="py-3 px-3 text-center text-emerald-500">{u.done_entries}</td>
                      <td className="py-3 px-3 text-center" style={{ color:'var(--text)' }}>{parseFloat(u.total_planned||0).toFixed(1)}</td>
                      <td className="py-3 px-3 text-center" style={{ color:'var(--text)' }}>{parseFloat(u.total_actual||0).toFixed(1)}</td>
                      <td className="py-3 px-3"><div className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background:'var(--border)' }}><div className="h-full rounded-full" style={{ width:`${pct}%`, background:pct===100?'#10b981':'#2E539F' }}/></div><span className="text-xs w-8" style={{ color:'var(--text-secondary)' }}>{pct}%</span></div></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Time grid — Day / 3-Day / Week */}
      {!loading && (viewMode==='day'||viewMode==='3day'||viewMode==='week') && (
        <div className="flex-1 flex flex-col rounded-xl overflow-hidden min-h-0"
          style={{ background:'var(--surface)', border:'1px solid var(--border)', boxShadow:'0 2px 8px rgba(0,0,0,.07)' }}>

          {/* Fixed day headers */}
          <div className="flex-shrink-0 flex border-b" style={{ borderColor:'var(--border)' }}>
            <div className="flex-shrink-0 border-r" style={{ width:TIME_COL, borderColor:'var(--border)' }}/>
            {visibleDays.map((date,i) => {
              const isToday = isSameDay(date, new Date());
              const dow = date.getDay()===0 ? 6 : date.getDay()-1;
              return (
                <div key={i} className="flex-1 border-r last:border-r-0 text-center py-3" style={{ borderColor:'var(--border)' }}>
                  {/* Day name derived from the actual date — not from loop index */}
                  <p className="text-xs font-semibold uppercase tracking-widest" style={{ color:isToday?'#2E539F':'var(--text-secondary)' }}>
                    {format(date,'EEE').toUpperCase()}
                  </p>
                  <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-base font-bold mx-auto mt-0.5 ${isToday?'bg-brand-500 text-white':''}`}
                    style={!isToday?{color:'var(--text)'}:{}}>
                    {format(date,'d')}
                  </div>

                  {/* Due here — a deadline, not a block of time */}
                  {(dueByDate[format(date,'yyyy-MM-dd')] || []).slice(0,3).map(d => (
                    <p key={d.id} className="text-[10px] truncate px-1 mt-0.5 rounded"
                      title={`Due today: ${d.title}`}
                      style={{ background:'rgba(217,119,6,.12)', color:'#d97706' }}>
                      ⚑ {d.title}
                    </p>
                  ))}
                  {(dueByDate[format(date,'yyyy-MM-dd')] || []).length > 3 && (
                    <p className="text-[10px] mt-0.5" style={{ color:'var(--text-secondary)' }}>
                      +{dueByDate[format(date,'yyyy-MM-dd')].length - 3} more due
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Scrollable time grid */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden" style={{ minHeight:0 }}>
            <div className="flex relative" style={{ height:24*HOUR_PX }}>

              {/* Time labels */}
              <div className="flex-shrink-0 relative border-r" style={{ width:TIME_COL, borderColor:'var(--border)' }}>
                {HOURS.map(h=>(
                  <div key={h} className="absolute flex items-start justify-end pr-2"
                    style={{ top:h*HOUR_PX, height:HOUR_PX, width:TIME_COL }}>
                    {h>0 && (
                      <span className="-translate-y-2.5 select-none font-medium"
                        style={{ color:'var(--text-secondary)', fontSize:10 }}>
                        {h<12?`${h}:00 AM`:h===12?'12:00 PM':`${h-12}:00 PM`}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {visibleDays.map((date,i) => {
                const isToday = isSameDay(date, new Date());
                const dow = date.getDay()===0 ? 6 : date.getDay()-1;
                const allEntries = data?.days?.[dow]?.entries || [];

                // Auto-position virtual items without start_time starting at 8am
                let virtualSlot = 8*60;
                const displayEntries = allEntries.map(entry => {
                  if (entry.is_virtual && !entry.start_time) {
                    const sm = virtualSlot;
                    const em = sm + Math.max((parseFloat(entry.planned_hours)||1)*60, 60);
                    virtualSlot = em + 15;
                    return { ...entry, start_time:minsToTime(sm), end_time:minsToTime(em), _displayOnly:true };
                  }
                  return entry;
                });

                return (
                  <div key={i} className="flex-1 relative border-r last:border-r-0 cursor-crosshair"
                    style={{ borderColor:'var(--border)',
                             background: dragOverDay===dow ? 'rgba(46,83,159,.07)'
                                       : isToday ? 'rgba(46,83,159,.018)' : undefined }}
                    onClick={e=>handleGridClick(e,dow)}
                    onDragOver={e=>{ e.preventDefault(); if (dragOverDay!==dow) setDragOverDay(dow); }}
                    onDragLeave={()=>setDragOverDay(d => d===dow ? null : d)}
                    onDrop={e=>handleDrop(e, dow)}>

                    {/* Hour lines */}
                    {HOURS.map(h=>(
                      <div key={h} className="absolute w-full pointer-events-none"
                        style={{ top:h*HOUR_PX, height:1, background:h===0?'transparent':'var(--border)', opacity:h%6===0?0.7:0.3 }}/>
                    ))}
                    {/* Half-hour lines */}
                    {HOURS.map(h=>(
                      <div key={`hh${h}`} className="absolute w-full pointer-events-none"
                        style={{ top:h*HOUR_PX+HOUR_PX/2, height:1, background:'var(--border)', opacity:0.12 }}/>
                    ))}

                    {/* Current time indicator */}
                    {isToday && (
                      <div className="absolute w-full pointer-events-none z-20" style={{ top:nowTop }}>
                        <div className="relative flex items-center">
                          <div className="absolute -left-1 w-2.5 h-2.5 rounded-full bg-red-500"/>
                          <div className="w-full border-t-2 border-red-500"/>
                        </div>
                      </div>
                    )}

                    {/* Event cards */}
                    {displayEntries.map(entry=>(
                      <EventCard key={entry.card_key || entry.id} entry={entry} onDragStart={handleDragStart}
                        onEdit={openEdit} onDelete={handleDelete}
                        onStatusChange={handleStatus} onToggleDone={handleToggleDone} onConfirm={handleConfirm}
                        onCardClick={handleCardClick}/>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {addingDay !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>{setAddingDay(null);setEditEntry(null);}}/>
          <div className="relative rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden max-h-[90vh] flex flex-col"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <h3 className="font-bold text-base" style={{ color:'var(--text)' }}>
                {editEntry?'Edit Entry':`Add — ${DAY_LONG[addingDay]}`}
              </h3>
              {form.start_time && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{fmt12(form.start_time)}</p>}
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              {!editEntry && (
                <div className="rounded-xl p-3 border flex items-center justify-between"
                  style={{ borderColor:form.useSpan?'#2E539F':'var(--border)', background:form.useSpan?'rgba(46,83,159,.04)':'var(--surface-alt)' }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color:'var(--text)' }}>Schedule across multiple days</p>
                    <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{form.useSpan?'Select days below':'Off — one day only'}</p>
                  </div>
                  <button type="button" onClick={()=>setForm(f=>({...f,useSpan:!f.useSpan,spanDays:!f.useSpan?[addingDay]:[]}))}
                    className="relative w-11 h-6 rounded-full flex-shrink-0" style={{ background:form.useSpan?'#2E539F':'var(--border)' }}>
                    <span className="absolute top-1 w-4 h-4 rounded-full bg-white shadow" style={{ left:form.useSpan?22:4 }}/>
                  </button>
                </div>
              )}
              {/* No entry type to choose: the Workplan plans ACTIVITIES
                  (phase68). A task is a container — you plan the activities
                  inside it — and a card with nothing behind it has no record
                  to roll up into a project. */}
              <div><label className="label">Activity</label>
                <select className="select" value={form.activity_id} onChange={e=>{
                  const a=(data?.available_activities||[]).find(x=>x.id===e.target.value);
                  setForm(f=>({...f,activity_id:e.target.value,task_id:a?.task_id||'',planned_hours:a?.estimated_hours||'1'}));
                }}>
                  <option value="">— Select an activity —</option>
                  {(data?.available_activities||[]).map(a=><option key={a.id} value={a.id}>{a.title} · {a.task_title}</option>)}
                </select></div>

              {!editEntry && form.useSpan ? (
                <div className="space-y-3 rounded-xl p-3 border" style={{ borderColor:'#2E539F', background:'rgba(46,83,159,.03)' }}>
                  <div className="flex gap-1.5 flex-wrap">
                    {DAY_SHORT.map((d,i)=>(
                      <button key={i} type="button" onClick={()=>setForm(f=>({...f,spanDays:f.spanDays.includes(i)?f.spanDays.filter(x=>x!==i):[...f.spanDays,i].sort()}))}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors"
                        style={{ background:form.spanDays.includes(i)?'#2E539F':'var(--surface)', color:form.spanDays.includes(i)?'#fff':'var(--text-secondary)', borderColor:form.spanDays.includes(i)?'#2E539F':'var(--border)' }}>
                        {d}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="w-28"><label className="label">Hours/day</label>
                      <input type="number" min="0.5" max="16" step="0.5" className="input" value={form.hoursPerDay} onChange={e=>setForm(f=>({...f,hoursPerDay:e.target.value}))}/></div>
                    {form.spanDays.length>0 && <p className="text-sm font-bold pb-1" style={{ color:'#2E539F' }}>= {(parseFloat(form.hoursPerDay||0)*form.spanDays.length).toFixed(1)}h total</p>}
                  </div>
                  <div><label className="label">Status</label>
                    <select className="select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                      {PLAN_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                    </select></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="label">Start time <span style={{ fontWeight:'normal', color:'var(--text-secondary)' }}>(optional)</span></label>
                      <input type="time" className="input" value={form.start_time} onChange={e=>{const st=e.target.value;setForm(f=>{let hrs=f.planned_hours;if(st&&f.end_time){const diff=(timeToMins(f.end_time)-timeToMins(st))/60;if(diff>0)hrs=String(Math.round(diff*10)/10);}return{...f,start_time:st,planned_hours:hrs};});}}/></div>
                    <div><label className="label">End time <span style={{ fontWeight:'normal', color:'var(--text-secondary)' }}>(optional)</span></label>
                      <input type="time" className="input" value={form.end_time} onChange={e=>{const et=e.target.value;setForm(f=>{let hrs=f.planned_hours;if(f.start_time&&et){const diff=(timeToMins(et)-timeToMins(f.start_time))/60;if(diff>0)hrs=String(Math.round(diff*10)/10);}return{...f,end_time:et,planned_hours:hrs};});}}/></div>
                  </div>
                  {(form.activity_id || form.task_id) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="label">Start date</label>
                        <input type="date" className="input" value={form.start_date}
                          onChange={e=>setForm(f=>({...f,start_date:e.target.value}))}/></div>
                      <div><label className="label">Due date</label>
                        <input type="date" className="input" value={form.due_date}
                          onChange={e=>setForm(f=>({...f,due_date:e.target.value}))}/></div>
                    </div>
                  )}
                  {/* No repeat control. An entry marks ONE DAY; the rule
                      belongs to the activity (Activities → the activity →
                      Repeats), which is where a standing duty lives and the
                      only place it can be set without planning a week first. */}
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="label">Planned hrs</label>
                      <input type="number" min="0" step="0.5" className="input" value={form.planned_hours} onChange={e=>setForm(f=>({...f,planned_hours:e.target.value}))}/></div>
                    <div><label className="label">Status</label>
                      <select className="select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                        {PLAN_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                      </select></div>
                  </div>
                </div>
              )}
              <div><label className="label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 flex-shrink-0" style={{ borderColor:'var(--border)' }}>
              <button onClick={()=>{setAddingDay(null);setEditEntry(null);}} className="btn-secondary">Cancel</button>
              <button onClick={handleSave}
                disabled={saving||!form.activity_id||(form.useSpan&&form.spanDays.length===0)}
                className="btn-primary flex items-center gap-2">
                {saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                {editEntry?'Update':form.useSpan?`Add to ${form.spanDays.length} days`:'Add to Workplan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDel(null)}/>
          <div className="relative rounded-2xl shadow-2xl w-full max-w-sm z-10 overflow-hidden"
            style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <div className="px-6 py-5">
              <p className="font-bold text-base mb-1" style={{ color:'var(--text)' }}>Remove from Workplan?</p>
              <p className="text-sm" style={{ color:'var(--text-secondary)' }}>
                This removes the entry from your workplan. The task or activity itself is not deleted.
              </p>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setConfirmDel(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={confirmDelete}
                className="text-sm px-4 py-2 rounded-lg font-semibold bg-red-500 hover:bg-red-600 text-white transition-colors">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {detailEntry && (
        <DetailModal entry={detailEntry} data={detailData} loading={detailLoad}
          onClose={()=>{setDetailEntry(null);setDetailData(null);}}
          onStatusChange={handleStatus} onToggleDone={handleToggleDone}/>
      )}
    </div>
  );
}
